import { Link } from '@tanstack/react-router';
import { formatInTimeZone } from 'date-fns-tz';

import { OUTREACH_ANALYTICS_TIMEZONE } from '@apps/web/components/admin/outreach_observability/constants';
import { humanizeJudgeLabel } from '@apps/web/components/admin/outreach_observability/judge_quality_panel';
import { Badge } from '@apps/web/components/ui/badge';
import { cn } from '@apps/web/lib/utils';
import { type JudgeFlaggedRun } from '@libraries/typescript/api_client';

// Grade badge styling: calm for great/ok, severity (amber/red) for bad/urgent,
// matching the grade colors in the quality panel.
const GRADE_BADGE_CLASSES: Record<string, string> = {
    great: 'border-transparent bg-secondary text-secondary-foreground',
    ok: 'border-transparent bg-secondary text-secondary-foreground',
    bad: 'border-amber-500/60 text-amber-600 dark:text-amber-400',
    urgent: 'border-transparent bg-destructive text-white',
};

function formatCreatedAt(iso: string): string {
    return formatInTimeZone(
        new Date(iso),
        OUTREACH_ANALYTICS_TIMEZONE,
        'M/d h:mm a',
    );
}

function GradeBadge({ grade }: { readonly grade: string | null }) {
    if (grade === null) {
        return <span className="text-muted-foreground">—</span>;
    }
    return (
        <span
            className={cn(
                'inline-flex w-fit items-center rounded-md border px-2 py-0.5 text-xs font-medium',
                GRADE_BADGE_CLASSES[grade] ?? 'text-foreground',
            )}
        >
            {humanizeJudgeLabel(grade)}
        </span>
    );
}

export function JudgeFlaggedRunsTable({
    runs,
}: {
    readonly runs: Array<JudgeFlaggedRun>;
}) {
    if (runs.length === 0) {
        return (
            <div className="text-muted-foreground py-6 text-center text-sm">
                No flagged runs in the window.
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-2">
            <div className="text-muted-foreground text-xs">
                Newest {runs.length} flagged runs (last 14 days).
            </div>
            <div className="max-h-96 overflow-auto rounded-md border">
                <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0">
                        <tr className="text-muted-foreground text-left">
                            <th className="px-3 py-2 font-medium">Time</th>
                            <th className="px-3 py-2 font-medium">Org</th>
                            <th className="px-3 py-2 font-medium">Grade</th>
                            <th className="px-3 py-2 font-medium">Flags</th>
                            <th className="px-3 py-2 font-medium">Summary</th>
                            <th className="px-3 py-2 font-medium">Links</th>
                        </tr>
                    </thead>
                    <tbody>
                        {runs.map((run) => (
                            <tr
                                // A conversation can accrue multiple judge notes
                                // over 14 days, so the conversation id alone is
                                // not unique — key by note timestamp too.
                                key={`${run.agent_conversation_id}:${run.created_at}`}
                                className="border-t align-top"
                            >
                                <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                                    {formatCreatedAt(run.created_at)}
                                </td>
                                <td className="px-3 py-2">
                                    {run.org_name ?? '—'}
                                </td>
                                <td className="px-3 py-2">
                                    <GradeBadge grade={run.quality_grade} />
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex flex-wrap gap-1">
                                        {run.labels.map((label) => (
                                            <Badge
                                                key={label}
                                                variant="outline"
                                            >
                                                {humanizeJudgeLabel(label)}
                                            </Badge>
                                        ))}
                                    </div>
                                </td>
                                <td className="text-muted-foreground max-w-xs truncate px-3 py-2">
                                    <span title={run.summary}>
                                        {run.summary}
                                    </span>
                                </td>
                                <td className="px-3 py-2">
                                    <Link
                                        to="/admin/agent-traces/$conversationId"
                                        params={{
                                            conversationId:
                                                run.agent_conversation_id,
                                        }}
                                        className="text-primary hover:underline"
                                    >
                                        Trace
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
